<template>
  <div class="w-full">
    <CatalogCombobox
      :label="label"
      :display-value="displayValue"
      :empty-text="emptyText"
      placeholder="Buscar setor"
      :sections="sections"
      :search="search"
      :disabled="disabled || !healthUnitId"
      :clearable="clearable"
      :no-results-text="
        search.trim()
          ? `Nenhum setor encontrado para “${search.trim()}”.`
          : 'Nenhum setor cadastrado nesta unidade.'
      "
      :footer-note="footerNote"
      :test-id="testId"
      @update:search="search = $event"
      @select="onSelect"
      @clear="emitValue(null)"
      @open="onOpen"
    />
    <p
      v-if="createdMessage"
      class="mt-1 text-xs text-green-700"
      :data-testid="`${testId}-created-message`"
    >
      {{ createdMessage }}
    </p>
    <p v-if="loadError" class="mt-1 text-xs text-red-600" :data-testid="`${testId}-load-error`">
      {{ loadError }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CatalogCombobox from './CatalogCombobox.vue'
import type { CatalogOption, CatalogSection } from './catalogTypes'
import { useSectorStore } from '@/pinia-store/useSectorStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { useErrorToast } from '@/composables/useErrorToast'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { catalogDisplayName, isRefArchived } from '@/utils/stationDisplay'
import {
  findSectorByName,
  matchesCatalogSearch,
  normalizeCatalogName,
  pendingSectorSuggestions
} from '@/utils/sectorSpecialty'
import type { Sector } from '@/Network/Types/Responses/SectorResponses'
import type { StationSectorRef } from '@/Network/Types/Responses/StationResponses'

const props = withDefaults(
  defineProps<{
    modelValue: StationSectorRef | null
    healthUnitId: number | null
    label?: string
    emptyText?: string
    disabled?: boolean
    clearable?: boolean
    testId?: string
  }>(),
  {
    label: 'Setor',
    emptyText: '— sem setor',
    disabled: false,
    clearable: true,
    testId: 'sector-combobox'
  }
)

const emit = defineEmits<{ 'update:modelValue': [value: StationSectorRef | null] }>()

const sectorStore = useSectorStore()
const permissionsStore = usePermissionsStore()
const { notifyError } = useErrorToast()
const search = ref('')
const createdMessage = ref<string | null>(null)

const slug = (name: string) =>
  normalizeCatalogName(name)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const canCreate = computed(
  () =>
    !!props.healthUnitId &&
    permissionsStore.check({
      permission: AuthorizationPermissions.HealthUnitEditing,
      resource: {
        objectType: AuthorizationResourcesAndSubjects.HealthUnit,
        objectId: props.healthUnitId
      }
    })
)

watch(
  () => props.healthUnitId,
  (unitId) => {
    // Trocar de unidade descarta a busca e o aviso de criação da unidade anterior.
    search.value = ''
    createdMessage.value = null
    if (unitId) void sectorStore.fetchByHealthUnit(unitId)
  },
  { immediate: true }
)

const allSectors = computed(() => sectorStore.sectorsOf(props.healthUnitId))
const activeSectors = computed(() => sectorStore.activeSectorsOf(props.healthUnitId))
const displayValue = computed(() => catalogDisplayName(props.modelValue))
const loadError = computed(() =>
  props.healthUnitId ? (sectorStore.loadErrorByUnit[props.healthUnitId] ?? null) : null
)
const exactMatch = computed(() => findSectorByName(search.value, allSectors.value))
const footerNote = computed(() =>
  canCreate.value ? null : '🔒 Criar setor exige permissão de editar a unidade'
)

const sections = computed<CatalogSection[]>(() => {
  const result: CatalogSection[] = []
  if (props.modelValue && props.clearable) {
    result.push({
      key: 'clear',
      title: null,
      options: [
        {
          key: 'clear',
          label: '— Sem setor',
          kind: 'clear',
          testId: `${props.testId}-clear-option`
        }
      ]
    })
  }
  result.push({
    key: 'unit',
    title: 'Setores da unidade',
    options: activeSectors.value
      .filter((sector) => matchesCatalogSearch(sector.name, search.value))
      .map((sector) => ({
        key: `sector:${sector.id}`,
        label: sector.name,
        kind: 'option',
        testId: `${props.testId}-option-${sector.id}`
      }))
  })
  if (!canCreate.value) return result
  const suggested = pendingSectorSuggestions(sectorStore.suggestions, allSectors.value).filter(
    (name) => matchesCatalogSearch(name, search.value)
  )
  if (suggested.length) {
    result.push({
      key: 'suggested',
      title: 'Sugeridos · ainda não cadastrados',
      options: suggested.map((name) => ({
        key: `suggest:${name}`,
        label: `＋ ${name}`,
        kind: 'action',
        testId: `${props.testId}-suggestion-${slug(name)}`
      }))
    })
  }
  const typed = search.value.trim()
  if (typed && !exactMatch.value) {
    result.push({
      key: 'create',
      title: null,
      options: [
        {
          key: 'create',
          label: `＋ Criar setor “${typed}” nesta unidade`,
          kind: 'action',
          testId: `${props.testId}-create-option`
        }
      ]
    })
  }
  if (exactMatch.value && isRefArchived(exactMatch.value)) {
    result.push({
      key: 'reactivate',
      title: null,
      options: [
        {
          key: `reactivate:${exactMatch.value.id}`,
          label: `Reativar e usar “${exactMatch.value.name}”`,
          kind: 'action',
          testId: `${props.testId}-reactivate-option`
        }
      ]
    })
  }
  return result
})

async function onOpen(): Promise<void> {
  if (props.healthUnitId) await sectorStore.fetchByHealthUnit(props.healthUnitId)
  if (canCreate.value) await sectorStore.fetchSuggestions()
}

function emitValue(sector: Sector | StationSectorRef | null): void {
  search.value = ''
  emit(
    'update:modelValue',
    sector ? { id: sector.id, name: sector.name, archivedAt: sector.archivedAt ?? null } : null
  )
}

async function onSelect(option: CatalogOption): Promise<void> {
  createdMessage.value = null
  const unitId = props.healthUnitId
  if (!unitId) return
  if (option.key === 'clear') return emitValue(null)
  if (option.key.startsWith('sector:')) {
    const sector = activeSectors.value.find((item) => `sector:${item.id}` === option.key)
    if (sector) emitValue(sector)
    return
  }
  try {
    if (option.key.startsWith('reactivate:')) {
      emitValue(await sectorStore.unarchiveSector(unitId, Number(option.key.split(':')[1])))
      return
    }
    const name = option.key.startsWith('suggest:')
      ? option.key.slice('suggest:'.length)
      : search.value.trim()
    const existing = findSectorByName(name, allSectors.value)
    if (existing) {
      // Homônimo arquivado nunca vira duplicata: o caminho é "Reativar e usar".
      if (!isRefArchived(existing)) emitValue(existing)
      return
    }
    const created = await sectorStore.createSector(unitId, name)
    createdMessage.value = `Setor “${created.name}” criado nesta unidade. Ele também aparece em Unidades › Setores.`
    emitValue(created)
  } catch (error) {
    notifyError(error, 'Não foi possível criar o setor')
  }
}
</script>
